import { QUESTIONS } from '../constants/questions/index';

/**
 * Gets the index of a question by its ID
 */
export function getQuestionIndexById(questionId) {
  return QUESTIONS.findIndex(q => q.id === questionId);
}

/**
 * Determines the next question index based on current question and answer
 * @param {number} currentIndex - Current question index
 * @param {any} currentAnswer - The answer to the current question
 * @param {object} allAnswers - All answers so far
 * @returns {number} The next question index, or -1 if at end
 */
export function getNextQuestionIndex(currentIndex, currentAnswer, allAnswers = {}) {
  if (currentIndex >= QUESTIONS.length - 1) {
    return -1; // End of survey
  }

  const currentQuestion = QUESTIONS[currentIndex];

  // Check if question has conditional navigation
  if (currentQuestion.nextQuestion) {
    let nextQuestionId;

    // nextQuestion can be a function or a string
    if (typeof currentQuestion.nextQuestion === 'function') {
      nextQuestionId = currentQuestion.nextQuestion(currentAnswer, allAnswers);
    } else {
      nextQuestionId = currentQuestion.nextQuestion;
    }

    // If nextQuestion returns null/undefined, go to next in sequence
    if (!nextQuestionId) {
      return currentIndex + 1;
    }

    // Find the question by ID
    const nextIndex = getQuestionIndexById(nextQuestionId);

    if (nextIndex === -1) {
      console.error(`Question with id "${nextQuestionId}" not found`);
      return currentIndex + 1; // Fallback to next question
    }

    return nextIndex;
  }

  // Default: go to next question
  return currentIndex + 1;
}

/**
 * Gets the previous question index from navigation history
 * @param {number[]} history - Array of visited question indices
 * @returns {number} The previous question index, or 0 if at start
 */
export function getPreviousQuestionIndex(history) {
  if (history.length <= 1) {
    return 0;
  }
  // Return the second-to-last item (last item is current question)
  return history[history.length - 2];
}

/**
 * Calculates how many questions remain in the survey based on current position and answers
 * Takes into account conditional navigation flows
 * @param {number} currentStep - Current question index
 * @param {object} answers - All answers so far
 * @param {array} questions - All survey questions
 * @returns {{remaining: number, isEstimate: boolean}} Remaining questions count and whether it's an estimate
 */
export function calculateRemainingQuestions(currentStep, answers, questions) {
  // If we're at or past the end, no questions remain
  if (currentStep >= questions.length - 1) {
    return { remaining: 0, isEstimate: false };
  }

  let nextIndex = currentStep + 1;
  const visited = new Set([currentStep]);
  let isEstimate = false;
  const MAX_ITERATIONS = 1000; // Prevent infinite loops
  let iterations = 0;

  // Simulate forward navigation from next question
  while (nextIndex !== -1 && nextIndex < questions.length && iterations < MAX_ITERATIONS) {
    iterations++;

    // Avoid infinite loops
    if (visited.has(nextIndex)) {
      break;
    }

    visited.add(nextIndex);

    const question = questions[nextIndex];

    // If this question has conditional navigation, check if we can determine next step
    if (question.nextQuestion && typeof question.nextQuestion === 'function') {
      // We need the answer to this question to know where to go next
      const answer = answers[question.id];

      // If we don't have an answer yet, we can't accurately predict the flow
      if (answer === undefined || answer === null || answer === '') {
        // Mark as estimate and count conservatively (assume linear flow from here)
        isEstimate = true;
        const remainingLinear = questions.length - nextIndex - 1;
        return {
          remaining: visited.size - 1 + remainingLinear,
          isEstimate: true
        };
      }

      // Try to get next question using the conditional logic
      try {
        nextIndex = getNextQuestionIndex(nextIndex, answer, answers);
      } catch (error) {
        // If there's an error in the conditional logic, fall back to linear
        isEstimate = true;
        const remainingLinear = questions.length - nextIndex - 1;
        return {
          remaining: visited.size - 1 + remainingLinear,
          isEstimate: true
        };
      }
    } else {
      // No conditional logic, just follow getNextQuestionIndex
      const answer = answers[question.id];
      nextIndex = getNextQuestionIndex(nextIndex, answer, answers);
    }
  }

  // visited includes current question, so subtract 1 to get remaining count
  const remaining = visited.size - 1;

  return { remaining, isEstimate };
}
